import React from 'react';
import ListingBody from '@plone/volto/components/manage/Blocks/Listing/ListingBody';
import {
  withQueryString,
  withSearch,
} from '@plone/volto/components/manage/Blocks/Search/hocs';
import { withBlockExtensions } from '@plone/volto/helpers/Extensions';
import config from '@plone/volto/registry';
import cx from 'classnames';
import isEqual from 'lodash/isEqual';
import isFunction from 'lodash/isFunction';
import { useSelector } from 'react-redux';
import { compose } from 'redux';

import { specificVariationId } from '../../BlockTemplates/Listing';

import DefaultFacet from './facets/DefaultFacet';
import { searchViews } from './schema';

const getListingBodyVariation = (data) => {
  const { variations } = config.blocks.blocksConfig.listing;

  let variation =
    variations.find(({ id }) => id === specificVariationId) ||
    variations.find(({ isDefault }) => isDefault);

  if (!variation) variation = variations[0];

  return variation;
};

const isfunc = (obj) => isFunction(obj) || typeof obj === 'function';

const _filtered = (obj) =>
  Object.assign(
    {},
    ...Object.keys(obj).map((k) => {
      const reject = k !== 'properties' && !isfunc(obj[k]);
      return reject ? { [k]: obj[k] } : {};
    }),
  );

const blockPropsAreChanged = (prevProps, nextProps) => {
  const prev = _filtered(prevProps);
  const next = _filtered(nextProps);

  return isEqual(prev, next);
};

const applyDefaults = (data, root) => {
  const defaultQuery = [
    {
      i: 'path',
      o: 'plone.app.querystring.operation.string.absolutePath',
      v: root || '/',
    },
  ];

  const searchBySearchableText = data.query.filter(
    (item) => item['i'] === 'SearchableText',
  ).length;

  const sort_on = data?.sort_on
    ? { sort_on: data.sort_on }
    : searchBySearchableText === 0
      ? { sort_on: 'effective' }
      : {};
  const sort_order = data?.sort_order
    ? { sort_order: data.sort_order }
    : searchBySearchableText === 0
      ? { sort_order: 'descending' }
      : {};

  return {
    ...data,
    ...sort_on,
    ...sort_order,
    query: data?.query?.length ? data.query : defaultQuery,
  };
};

const SearchBlockView = (props) => {
  const { id, data, searchData, mode = 'view', className } = props;

  const dataListingBodyVariation = getListingBodyVariation(data).id;
  const [selectedView, setSelectedView] = React.useState(
    dataListingBodyVariation,
  );
  const [previousFilter, setPreviousFilter] = React.useState(data.filters);

  // in the block edit you can change the used listing block variation,
  // but it's cached here in the state. So we reset it.
  React.useEffect(() => {
    if (mode !== 'view') {
      setSelectedView(dataListingBodyVariation);
    }
  }, [dataListingBodyVariation, mode]);

  // Clear search results when filter changes in edit mode
  React.useEffect(() => {
    if (mode === 'edit' && data.filters !== previousFilter) {
      setPreviousFilter(data.filters);
    }
  }, [data.filters, mode, previousFilter]);

  const root = useSelector((state) => state.breadcrumbs.root);
  const listingBodyData = applyDefaults(searchData, root);

  const { variations } = config.blocks.blocksConfig.listing;
  const listingBodyVariation = variations.find(({ id }) => id === selectedView);
  const variationOverrides = ['audio', 'video', 'contentHub'].includes(
    data.filters,
  )
    ? { fullobjects: true }
    : {};

  const FiltersAndFacets = React.useMemo(
    () => searchViews[data.filters]?.facet || DefaultFacet,
    [data.filters],
  );

  // If filter changed in edit mode and results don't match, show loading or empty
  const shouldShowResults =
    mode !== 'edit' || data.filters === previousFilter || searchData.items;

  return (
    <div className={cx('block search', selectedView, className)}>
      <FiltersAndFacets
        {...props}
        key={data.filters}
        isEditMode={mode === 'edit'}
        selectedView={selectedView}
        setSelectedView={setSelectedView}
      >
        {shouldShowResults && (
          <ListingBody
            id={id}
            variation={{
              ...data,
              ...listingBodyVariation,
              ...variationOverrides,
            }}
            data={listingBodyData}
            path={props.path}
            isEditMode={mode === 'edit'}
          />
        )}
      </FiltersAndFacets>
    </div>
  );
};

export const SearchBlockViewComponent = compose(
  withBlockExtensions,
  (Component) => React.memo(Component, blockPropsAreChanged),
)(SearchBlockView);

export default withSearch()(withQueryString(SearchBlockViewComponent));
